import React, { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  CheckCircle,
  Download,
  Edit3,
  Package,
  Plus,
  Radio,
  RefreshCw,
  Save,
  Search,
  Trash2,
  Wrench,
  X,
  XCircle,
} from 'lucide-react';
import Sidebar from '../components/Sidebar';
import TopBar from '../components/TopBar';
import { deviceService, vehicleService } from '../services/api';
import { exportCsv } from '../utils/csv';
import { toast } from 'react-toastify';
import { format } from 'date-fns';

const emptyForm = {
  imei: '',
  model: '',
  brand: '',
  sim_card: '',
  carrier: '',
  status: 'stock',
  vehicle_id: '',
  supplier: '',
  purchase_value: '',
  purchased_at: '',
  installed_at: '',
  last_seen_at: '',
  firmware: '',
  notes: '',
};

const statusLabels = {
  stock: 'Estoque',
  installed: 'Instalado',
  maintenance: 'Manutencao',
  inactive: 'Inativo',
  lost: 'Perdido',
};

const statusClass = {
  stock: 'badge-info',
  installed: 'badge-success',
  maintenance: 'badge-warning',
  inactive: 'badge-danger',
  lost: 'badge-danger',
};

const statusIcon = {
  stock: Package,
  installed: CheckCircle,
  maintenance: Wrench,
  inactive: XCircle,
  lost: AlertTriangle,
};

const currency = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
});

const dateLabel = (value) => {
  if (!value) return '-';
  return format(new Date(String(value).includes('T') ? value : `${value}T00:00:00`), 'dd/MM/yyyy');
};

const toForm = (device) => ({
  imei: device.imei || '',
  model: device.model || '',
  brand: device.brand || '',
  sim_card: device.sim_card || '',
  carrier: device.carrier || '',
  status: device.status || 'stock',
  vehicle_id: device.vehicle_id || '',
  supplier: device.supplier || '',
  purchase_value: device.purchase_value || '',
  purchased_at: device.purchased_at || '',
  installed_at: device.installed_at || '',
  last_seen_at: device.last_seen_at ? String(device.last_seen_at).slice(0, 16) : '',
  firmware: device.firmware || '',
  notes: device.notes || '',
});

const Devices = () => {
  const [devices, setDevices] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingDevice, setEditingDevice] = useState(null);
  const [formData, setFormData] = useState(emptyForm);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);

    try {
      const [deviceResponse, vehicleResponse] = await Promise.all([
        deviceService.getAll(),
        vehicleService.getAll(),
      ]);
      setDevices(deviceResponse.data.data || []);
      setVehicles(vehicleResponse.data.data || []);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao carregar rastreadores');
    } finally {
      setLoading(false);
    }
  };

  const filteredDevices = useMemo(() => {
    const term = search.trim().toLowerCase();

    return devices.filter((device) => {
      const matchesStatus = statusFilter === 'all' || device.status === statusFilter;
      const matchesSearch = !term || [
        device.imei,
        device.model,
        device.brand,
        device.sim_card,
        device.carrier,
        device.supplier,
        device.vehicle?.name,
        device.vehicle?.plate,
      ].filter(Boolean).some((value) => String(value).toLowerCase().includes(term));

      return matchesStatus && matchesSearch;
    });
  }, [devices, search, statusFilter]);

  const stockCount = devices.filter((device) => device.status === 'stock').length;
  const installedCount = devices.filter((device) => device.status === 'installed').length;
  const maintenanceCount = devices.filter((device) => device.status === 'maintenance').length;
  const totalInventory = devices.reduce((sum, device) => sum + Number(device.purchase_value || 0), 0);

  const openCreateModal = () => {
    setEditingDevice(null);
    setFormData(emptyForm);
    setModalOpen(true);
  };

  const openEditModal = (device) => {
    setEditingDevice(device);
    setFormData(toForm(device));
    setModalOpen(true);
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((current) => {
      const next = { ...current, [name]: value };

      if (name === 'vehicle_id' && value && current.status === 'stock') {
        next.status = 'installed';
      }

      if (name === 'status' && value === 'stock') {
        next.vehicle_id = '';
        next.installed_at = '';
      }

      return next;
    });
  };

  const buildPayload = () => ({
    ...formData,
    vehicle_id: formData.vehicle_id || null,
    purchase_value: formData.purchase_value === '' ? 0 : Number(formData.purchase_value),
    last_seen_at: formData.last_seen_at || null,
  });

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);

    try {
      const payload = buildPayload();

      if (editingDevice) {
        const response = await deviceService.update(editingDevice.id, payload);
        setDevices((current) => current.map((device) => (
          device.id === editingDevice.id ? response.data.data : device
        )));
        toast.success('Rastreador atualizado com sucesso');
      } else {
        const response = await deviceService.create(payload);
        setDevices((current) => [response.data.data, ...current]);
        toast.success('Rastreador criado com sucesso');
      }

      setModalOpen(false);
      setEditingDevice(null);
      setFormData(emptyForm);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao salvar rastreador');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (device) => {
    const confirmed = window.confirm(`Remover o rastreador ${device.imei}?`);
    if (!confirmed) return;

    try {
      await deviceService.delete(device.id);
      setDevices((current) => current.filter((item) => item.id !== device.id));
      toast.success('Rastreador removido');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao remover rastreador');
    }
  };

  const handleExport = () => {
    exportCsv('rcrastro-rastreadores.csv', [
      { key: 'imei', label: 'IMEI' },
      { key: 'model', label: 'Modelo' },
      { key: 'brand', label: 'Marca' },
      { key: 'sim_card', label: 'Chip' },
      { key: 'carrier', label: 'Operadora' },
      { key: 'status', label: 'Status' },
      { key: 'vehicle', label: 'Veiculo' },
      { key: 'plate', label: 'Placa' },
      { key: 'supplier', label: 'Fornecedor' },
      { key: 'purchase_value', label: 'Valor' },
      { key: 'purchased_at', label: 'Compra' },
      { key: 'installed_at', label: 'Instalacao' },
      { key: 'last_seen_at', label: 'Ultimo sinal' },
    ], filteredDevices.map((device) => ({
      imei: device.imei,
      model: device.model || '',
      brand: device.brand || '',
      sim_card: device.sim_card || '',
      carrier: device.carrier || '',
      status: statusLabels[device.status] || device.status,
      vehicle: device.vehicle?.name || '',
      plate: device.vehicle?.plate || '',
      supplier: device.supplier || '',
      purchase_value: currency.format(Number(device.purchase_value || 0)),
      purchased_at: dateLabel(device.purchased_at),
      installed_at: dateLabel(device.installed_at),
      last_seen_at: dateLabel(device.last_seen_at),
    })));
    toast.success('CSV de rastreadores gerado');
  };

  return (
    <div className="flex h-screen bg-dark-950">
      <Sidebar />

      <div className="flex-1 flex flex-col overflow-hidden">
        <TopBar />

        <main className="flex-1 overflow-y-auto p-6">
          <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h1 className="text-2xl font-bold text-white">Rastreadores</h1>
              <p className="text-dark-400">Controle estoque, chips, IMEI e vinculo com veiculos.</p>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row">
              <button
                type="button"
                onClick={handleExport}
                className="btn-secondary flex items-center justify-center gap-2"
              >
                <Download className="h-4 w-4" />
                Exportar CSV
              </button>
              <button
                type="button"
                onClick={loadData}
                className="btn-secondary flex items-center justify-center gap-2"
              >
                <RefreshCw className="h-4 w-4" />
                Atualizar
              </button>
              <button
                type="button"
                onClick={openCreateModal}
                className="btn-primary flex items-center justify-center gap-2"
              >
                <Plus className="h-4 w-4" />
                Novo rastreador
              </button>
            </div>
          </div>

          <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-4">
            <div className="card">
              <div className="mb-2 flex items-center gap-2 text-dark-400">
                <Radio className="h-4 w-4" />
                Total
              </div>
              <p className="text-3xl font-bold text-white">{devices.length}</p>
            </div>
            <div className="card">
              <div className="mb-2 flex items-center gap-2 text-dark-400">
                <Package className="h-4 w-4" />
                Estoque
              </div>
              <p className="text-3xl font-bold text-primary-400">{stockCount}</p>
            </div>
            <div className="card">
              <div className="mb-2 flex items-center gap-2 text-dark-400">
                <CheckCircle className="h-4 w-4" />
                Instalados
              </div>
              <p className="text-3xl font-bold text-success-400">{installedCount}</p>
              <p className="mt-1 text-xs text-warning-400">{maintenanceCount} em manutencao</p>
            </div>
            <div className="card">
              <div className="mb-2 flex items-center gap-2 text-dark-400">
                <Package className="h-4 w-4" />
                Inventario
              </div>
              <p className="text-3xl font-bold text-white">{currency.format(totalInventory)}</p>
            </div>
          </div>

          <div className="card mb-6">
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_220px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-dark-400" />
                <input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  className="input-field pl-11"
                  placeholder="Buscar por IMEI, chip, modelo, veiculo ou fornecedor"
                />
              </div>
              <select
                value={statusFilter}
                onChange={(event) => setStatusFilter(event.target.value)}
                className="input-field"
              >
                <option value="all">Todos os status</option>
                {Object.entries(statusLabels).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="card p-0">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[1050px] text-left">
                <thead className="border-b border-dark-700 text-sm text-dark-400">
                  <tr>
                    <th className="px-5 py-4 font-medium">Rastreador</th>
                    <th className="px-5 py-4 font-medium">Chip</th>
                    <th className="px-5 py-4 font-medium">Veiculo</th>
                    <th className="px-5 py-4 font-medium">Datas</th>
                    <th className="px-5 py-4 font-medium">Status</th>
                    <th className="px-5 py-4 text-right font-medium">Acoes</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="6" className="px-5 py-10 text-center text-dark-400">
                        Carregando rastreadores...
                      </td>
                    </tr>
                  ) : filteredDevices.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="px-5 py-10 text-center text-dark-400">
                        <Radio className="mx-auto mb-2 h-10 w-10 opacity-50" />
                        Nenhum rastreador encontrado
                      </td>
                    </tr>
                  ) : (
                    filteredDevices.map((device) => {
                      const StatusIcon = statusIcon[device.status] || Radio;

                      return (
                        <tr key={device.id} className="border-b border-dark-800 text-sm last:border-0">
                          <td className="px-5 py-4">
                            <div className="flex items-start gap-3">
                              <div className="mt-1 rounded-lg bg-dark-700 p-2 text-primary-400">
                                <Radio className="h-4 w-4" />
                              </div>
                              <div>
                                <div className="font-semibold text-white">{device.imei}</div>
                                <div className="text-dark-400">{[device.brand, device.model].filter(Boolean).join(' ') || 'Modelo nao informado'}</div>
                                <div className="text-xs text-dark-500">Firmware {device.firmware || '-'}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-5 py-4 text-dark-300">
                            <div>{device.sim_card || 'Sem chip'}</div>
                            <div className="text-xs text-dark-500">{device.carrier || 'Operadora nao informada'}</div>
                          </td>
                          <td className="px-5 py-4 text-dark-300">
                            <div className="font-medium text-white">{device.vehicle?.name || 'Nao vinculado'}</div>
                            <div className="text-xs text-dark-500">{device.vehicle?.plate || '-'}</div>
                          </td>
                          <td className="px-5 py-4 text-dark-300">
                            <div>Compra {dateLabel(device.purchased_at)}</div>
                            <div className="text-xs text-dark-500">Instalacao {dateLabel(device.installed_at)}</div>
                            <div className="text-xs text-dark-500">Ultimo sinal {dateLabel(device.last_seen_at)}</div>
                          </td>
                          <td className="px-5 py-4">
                            <span className={`badge ${statusClass[device.status] || 'badge-info'} inline-flex items-center gap-1`}>
                              <StatusIcon className="h-3 w-3" />
                              {statusLabels[device.status] || device.status}
                            </span>
                          </td>
                          <td className="px-5 py-4">
                            <div className="flex justify-end gap-2">
                              <button
                                type="button"
                                onClick={() => openEditModal(device)}
                                className="rounded-lg p-2 text-primary-400 transition-colors hover:bg-dark-700"
                                title="Editar"
                              >
                                <Edit3 className="h-4 w-4" />
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDelete(device)}
                                className="rounded-lg p-2 text-danger-400 transition-colors hover:bg-dark-700"
                                title="Remover"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </main>
      </div>

      {modalOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 p-4">
          <div className="max-h-[90vh] w-full max-w-4xl overflow-y-auto rounded-lg border border-dark-700 bg-dark-900 shadow-2xl">
            <div className="flex items-center justify-between border-b border-dark-800 p-5">
              <div>
                <h2 className="text-lg font-bold text-white">
                  {editingDevice ? 'Editar rastreador' : 'Novo rastreador'}
                </h2>
                <p className="text-sm text-dark-400">Dados do equipamento, chip e instalacao.</p>
              </div>
              <button
                type="button"
                onClick={() => setModalOpen(false)}
                className="rounded-lg p-2 text-dark-400 transition-colors hover:bg-dark-800 hover:text-white"
                title="Fechar"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5 p-5">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-2 block text-sm font-medium text-dark-300">IMEI</label>
                  <input
                    name="imei"
                    value={formData.imei}
                    onChange={handleChange}
                    className="input-field"
                    placeholder="860000000000000"
                    required
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-dark-300">Status</label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    className="input-field"
                  >
                    {Object.entries(statusLabels).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-dark-300">Marca</label>
                  <input
                    name="brand"
                    value={formData.brand}
                    onChange={handleChange}
                    className="input-field"
                    placeholder="Concox, Cobain..."
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-dark-300">Modelo</label>
                  <input
                    name="model"
                    value={formData.model}
                    onChange={handleChange}
                    className="input-field"
                    placeholder="J16, TK905..."
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-dark-300">Chip</label>
                  <input
                    name="sim_card"
                    value={formData.sim_card}
                    onChange={handleChange}
                    className="input-field"
                    placeholder="ICCID ou numero"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-dark-300">Operadora</label>
                  <input
                    name="carrier"
                    value={formData.carrier}
                    onChange={handleChange}
                    className="input-field"
                    placeholder="Vivo, Claro, TIM..."
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-dark-300">Veiculo vinculado</label>
                  <select
                    name="vehicle_id"
                    value={formData.vehicle_id}
                    onChange={handleChange}
                    className="input-field"
                  >
                    <option value="">Sem vinculo</option>
                    {vehicles.map((vehicle) => (
                      <option key={vehicle.id} value={vehicle.id}>
                        {vehicle.name} - {vehicle.plate}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-dark-300">Firmware</label>
                  <input
                    name="firmware"
                    value={formData.firmware}
                    onChange={handleChange}
                    className="input-field"
                    placeholder="1.0.7"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-dark-300">Fornecedor</label>
                  <input
                    name="supplier"
                    value={formData.supplier}
                    onChange={handleChange}
                    className="input-field"
                    placeholder="Fornecedor"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-dark-300">Valor de compra</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    name="purchase_value"
                    value={formData.purchase_value}
                    onChange={handleChange}
                    className="input-field"
                    placeholder="120.00"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-dark-300">Data da compra</label>
                  <input
                    type="date"
                    name="purchased_at"
                    value={formData.purchased_at}
                    onChange={handleChange}
                    className="input-field"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-dark-300">Data da instalacao</label>
                  <input
                    type="date"
                    name="installed_at"
                    value={formData.installed_at}
                    onChange={handleChange}
                    className="input-field"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-dark-300">Ultimo sinal</label>
                  <input
                    type="datetime-local"
                    name="last_seen_at"
                    value={formData.last_seen_at}
                    onChange={handleChange}
                    className="input-field"
                  />
                </div>
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-dark-300">Observacoes</label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  className="input-field min-h-24"
                  placeholder="Local de instalacao, lacre, observacoes tecnicas..."
                />
              </div>

              <div className="flex flex-col-reverse gap-3 border-t border-dark-800 pt-5 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={() => setModalOpen(false)}
                  className="btn-secondary"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="btn-primary flex items-center justify-center gap-2"
                >
                  <Save className="h-4 w-4" />
                  {saving ? 'Salvando...' : 'Salvar rastreador'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Devices;
